import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { request } from '../../utils/remote';
import API from '../../utils/constant';
import { Badge, Button, LoadingState } from '../../school_panel/components/ui';

// Drawer section on Admin › RAG List: attach a book's PDF so students see the
// real page with the cited lines highlighted. School books can always take one;
// any other book only after an admin allows it (for books we have the right to
// show). Superuser-only API; the shared axios instance adds the auth header.

type Verdict = 'enabled' | 'needs_review' | 'unreadable_text' | 'no_text_layer' | 'no_pdf';

type Calibration = { verdict: Verdict; offset: number | null; agreement: number; samples: number; placed?: number };

type PageViewStatus = {
  book_id: number;
  has_pdf: boolean;
  pdf_page_count: number | null;
  page_offset: number;
  page_view_enabled: boolean;
  page_view_allowed?: boolean;
  calibration: Calibration | null;
  file_deleted?: boolean;
};

type Busy = null | { kind: 'upload'; percent: number } | { kind: 'check' } | { kind: 'remove' } | { kind: 'allow' };
type Confirm = null | 'remove' | 'replace' | 'allow';

export const MAX_PDF_BYTES = 300 * 1024 * 1024;

const base = (id: number) => `/api/textbook/${id}/page-view/`;
const pct = (x: number) => `${Math.round(x * 100)}%`;

const offsetPhrase = (n: number) => (n === 0 ? 'on the printed page number'
  : `${Math.abs(n)} page${Math.abs(n) === 1 ? '' : 's'} ${n > 0 ? 'after' : 'before'} the printed page number`);

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

// Passages pinned to a page (older servers send only the share).
const placedOf = (c: Calibration) => c.placed ?? Math.round(c.agreement * c.samples);

// What the page check found, in plain words.
function verdictMessage(c: Calibration): string {
  switch (c.verdict) {
    case 'enabled': {
      const rest = c.samples - placedOf(c);
      return `Pages found for ${placedOf(c)} of ${c.samples} searchable passages (${pct(c.agreement)}), mostly ${offsetPhrase(c.offset ?? 0)}. Students now see real pages${rest > 0 ? `; the other ${rest} show text only` : ''}.`;
    }
    case 'needs_review':
      return `Only ${placedOf(c)} of ${c.samples} searchable passages (${pct(c.agreement)}) could be placed on a page, so the page view stays off. Check that this PDF is the same edition as the book’s text.`;
    case 'unreadable_text':
      return 'This PDF’s text can’t be read: its fonts don’t record which letters they show, which is common in copies re-saved by download sites. The page view stays off. Upload the original Ministry of Education PDF, or run this copy through OCR first.';
    case 'no_text_layer':
      return 'This PDF is a scan without selectable text, so passages can’t be found on it. The page view stays off. Upload a PDF that has a text layer.';
    default:
      return 'The stored PDF is missing. Upload it again.';
  }
}

const UPLOAD_ERRORS: Record<string, string> = {
  not_a_pdf: 'That file isn’t a readable PDF.',
  file_too_large: 'That file is over 300 MB.',
  page_view_not_allowed: 'Allow real pages for this book first.',
};

function uploadError(err: any): string {
  const detail = err?.response?.data?.detail;
  if (detail && UPLOAD_ERRORS[detail]) return UPLOAD_ERRORS[detail];
  if (err?.response?.status === 413) return UPLOAD_ERRORS.file_too_large;
  return 'Couldn’t upload the PDF. Try again.';
}

const isPdf = (f: File) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');

export default function BookPageView({ book }: { book: { id: number; is_schoolbook: boolean } }) {
  const [status, setStatus] = useState<PageViewStatus | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<Busy>(null);
  const [result, setResult] = useState<Calibration | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLParagraphElement>(null);
  const removeWrap = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let live = true;
    setStatus(null); setLoadError(false);
    request(base(book.id))
      .then((s: PageViewStatus) => { if (live) setStatus(s); })
      .catch(() => { if (live) setLoadError(true); });
    return () => { live = false; };
  }, [book.id, attempt]);

  useEffect(() => {
    if (confirm) confirmRef.current?.focus();
  }, [confirm]);

  // After a failure the server may have changed anyway (e.g. the PDF attached
  // but the check timed out): re-read the status without a loading flash.
  const resync = useCallback(() => {
    request(base(book.id))
      .then((s: PageViewStatus) => { if (s?.book_id === book.id) setStatus(s); })
      .catch(() => {});
  }, [book.id]);

  const apply = (s: PageViewStatus) => {
    if (s?.book_id !== book.id) return; // a reply for another book: ignore it
    setStatus(s);
    setResult(s.calibration);
  };

  const start = () => { setError(null); setResult(null); setNote(null); setConfirm(null); };

  const upload = async (file: File) => {
    start();
    if (!isPdf(file)) { setError('Choose a PDF file.'); return; }
    if (file.size > MAX_PDF_BYTES) { setError(UPLOAD_ERRORS.file_too_large); return; }
    setBusy({ kind: 'upload', percent: 0 });
    const form = new FormData();
    form.append('file', file);
    try {
      const resp = await API.post(`api/textbook/${book.id}/page-view/pdf/`, form, {
        onUploadProgress: (e: { loaded: number; total?: number }) => {
          const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          // The server checks the pages once the bytes are in.
          setBusy(percent >= 100 ? { kind: 'check' } : { kind: 'upload', percent });
        },
      });
      apply(resp.data);
    } catch (err) {
      setError(uploadError(err));
      resync();
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const pickFile = () => {
    if (status?.page_view_enabled) { setConfirm('replace'); return; }
    fileInput.current?.click();
  };

  const recheck = async () => {
    start();
    setBusy({ kind: 'check' });
    try {
      apply(await request(`${base(book.id)}calibrate/`, { method: 'POST' }));
    } catch {
      setError('Couldn’t check the pages. Try again.');
      resync();
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    start();
    setBusy({ kind: 'remove' });
    try {
      const s: PageViewStatus = await request(`${base(book.id)}pdf/`, { method: 'DELETE' });
      apply(s);
      setNote(s.file_deleted
        ? 'The PDF was removed and deleted from the server.'
        : 'The PDF was removed from this book. The file is kept because another book uses it.');
    } catch {
      setError('Couldn’t remove the PDF. Try again.');
      resync();
    } finally {
      setBusy(null);
    }
  };

  const setAllowed = async (allowed: boolean) => {
    start();
    setBusy({ kind: 'allow' });
    try {
      apply(await request(base(book.id), { method: 'PATCH', body: { page_view_allowed: allowed } }));
      setResult(null);
    } catch {
      setError(allowed ? 'Couldn’t allow real pages. Try again.' : 'Couldn’t stop real pages. Try again.');
      resync();
    } finally {
      setBusy(null);
    }
  };

  const cancel = () => {
    const was = confirm;
    setConfirm(null);
    if (was === 'remove') setTimeout(() => removeWrap.current?.querySelector('button')?.focus(), 0);
  };

  if (loadError) {
    return (
      <Section>
        <div className="flex items-center justify-between gap-3">
          <p role="alert" className="text-sm text-danger-ink">Couldn’t load the page-view status.</p>
          <Button variant="ghost" size="sm" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
        </div>
      </Section>
    );
  }
  if (status === null) {
    return <Section><LoadingState label="Loading page view…" className="py-4" /></Section>;
  }

  // School books always may; other books only once allowed (older servers
  // don't send the flag and only let school books show pages).
  const allowed = status.page_view_allowed ?? book.is_schoolbook;
  const on = allowed && status.page_view_enabled;
  const busyLabel = busy?.kind === 'upload' ? `Uploading… ${busy.percent}%`
    : busy?.kind === 'check' ? 'Checking pages…'
      : busy?.kind === 'remove' ? 'Removing…'
        : busy?.kind === 'allow' ? 'Saving…' : null;
  // Screen readers hear phase changes and outcomes, not every percent.
  const announcement = busy?.kind === 'upload' ? 'Uploading the PDF'
    : busy?.kind === 'check' ? 'Checking the pages against the book'
      : busy?.kind === 'remove' ? 'Removing the PDF'
        : busy?.kind === 'allow' ? 'Saving'
        : result ? `Page check finished. ${verdictMessage(result)}`
          : note ? `Done. ${note}` : '';

  return (
    <Section>
      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={on ? 'success' : allowed && status.has_pdf ? 'warning' : 'neutral'}>
          {on ? 'On · students see real pages' : !allowed ? 'Off · not allowed for this book'
            : status.has_pdf ? 'Off · PDF not confirmed yet' : 'Off · no PDF yet'}
        </Badge>
      </div>
      {!book.is_schoolbook && !allowed && (
        <p className="mt-2 text-xs text-muted">This isn’t a high-school textbook, so it stays text-only unless you allow real pages. Allow them only if YADA has the right to show this book’s pages.</p>
      )}
      {status.has_pdf && (
        <p className="mt-2 text-xs text-muted">
          PDF attached · {status.pdf_page_count ?? '—'} pages{on ? ` · usual page shift ${signed(status.page_offset)}` : ''}
        </p>
      )}

      {result && (
        <p className={`mt-3 rounded-[12px] px-3 py-2 text-[13px] ${result.verdict === 'enabled' ? 'bg-success-bg text-ink' : 'bg-surface-2 text-ink'}`}>
          {verdictMessage(result)}
        </p>
      )}
      {note && <p className="mt-3 rounded-[12px] bg-surface-2 px-3 py-2 text-[13px] text-ink">{note}</p>}
      {error && <p role="alert" className="mt-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{error}</p>}

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,.pdf"
        aria-label="PDF file"
        className="sr-only"
        tabIndex={-1}
        disabled={busy !== null}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
      />

      {busyLabel ? (
        <p aria-hidden="true" className="mt-3 text-[13px] font-semibold text-ink">{busyLabel}</p>
      ) : confirm ? (
        <div role="group" aria-label={CONFIRM_LABEL[confirm]}
          className="mt-3 rounded-[12px] bg-surface-2 p-3">
          <p ref={confirmRef} tabIndex={-1} className="text-[13px] text-ink focus:outline-none">
            {CONFIRM_TEXT[confirm]}
          </p>
          <div className="mt-2 flex gap-2">
            {confirm === 'remove'
              ? <Button variant="danger" size="sm" onClick={remove}>Remove</Button>
              : confirm === 'allow'
                ? <Button variant="secondary" size="sm" onClick={() => setAllowed(true)}>Allow real pages</Button>
                : <Button variant="secondary" size="sm" onClick={() => { setConfirm(null); fileInput.current?.click(); }}>Choose new PDF</Button>}
            <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      ) : !allowed ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => { start(); setConfirm('allow'); }}>Allow real pages…</Button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" icon={<Upload size={14} aria-hidden />} onClick={pickFile}>
            {status.has_pdf ? 'Replace PDF' : 'Upload PDF'}
          </Button>
          {status.has_pdf && <Button variant="ghost" size="sm" onClick={recheck}>Re-check pages</Button>}
          {status.has_pdf && <span ref={removeWrap} className="contents"><Button variant="ghost" size="sm" onClick={() => setConfirm('remove')}>Remove PDF</Button></span>}
          {!book.is_schoolbook && <Button variant="ghost" size="sm" onClick={() => setAllowed(false)}>Stop real pages</Button>}
        </div>
      )}
    </Section>
  );
}

const CONFIRM_LABEL: Record<Exclude<Confirm, null>, string> = {
  remove: 'Confirm removing the PDF',
  replace: 'Confirm replacing the PDF',
  allow: 'Confirm allowing real pages',
};

const CONFIRM_TEXT: Record<Exclude<Confirm, null>, string> = {
  remove: 'Remove the PDF? Students go back to the text-only view for this book, and the file is deleted unless another book uses it.',
  replace: 'Replace the PDF? Students lose real pages for this book until the new file’s pages are confirmed.',
  allow: 'Allow real pages only if YADA has the right to show this book’s pages: an open licence, a Ministry of Education module, or permission from the publisher. Most university textbooks are copyrighted. Then upload its PDF.',
};

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section aria-label="Real page view" className="border-t border-hairline pt-4">
      <h3 className="text-[13px] font-semibold text-ink">Real page view</h3>
      <p className="mb-3 mt-0.5 text-xs text-muted">Students see the actual textbook page with the cited lines highlighted. Upload this book’s PDF; the pages are checked against the book’s text before it switches on.</p>
      {children}
    </section>
  );
}
